import {
  newSize,
  newTextPinComponent,
  newVector,
  fallbackCopy,
  newDropZonePinComponent,
  newImagePinComponent,
} from '../helpers';
import { DragAndDropPinLayoutTemplate, ImagePinComponent } from '../models';
import { stringToProsemirror } from '../tip-tap';

export function dragAndDropTitleOnlyLayout({
  question = fallbackCopy['dragAndDrop.title'],
  backgroundColor,
  backgroundImage,
}: {
  question?: string;
  backgroundColor?: string;
  backgroundImage?: string;
}): DragAndDropPinLayoutTemplate {
  return {
    key: 'TITLE_ONLY',
    backgroundColor,
    backgroundImage,
    layoutParts: {
      TITLE: newTextPinComponent({
        position: newVector(32, 32),
        size: newSize(896, 60),
        content: stringToProsemirror(question, {
          bold: true,
        }),
        order: 'a0',
        settings: {
          textStyleKey: 'TITLE',
          typographyKey: 'headline',
          horizontalAlignment: 'center',
        },
      }),
    },
    pinComponents: [],
  };
}

export function dragAndDropSingleDropZoneLayout({
  question = fallbackCopy['dragAndDrop.title'],
  backgroundColor,
  backgroundImage,
}: {
  question?: string;
  backgroundColor?: string;
  backgroundImage?: string;
}): DragAndDropPinLayoutTemplate {
  return {
    key: 'SINGLE_DROP_ZONE',
    backgroundColor,
    backgroundImage,
    layoutParts: {
      TITLE: newTextPinComponent({
        position: newVector(32, 32),
        size: newSize(896, 60),
        content: stringToProsemirror(question, {
          bold: true,
        }),
        order: 'a0',
        settings: {
          textStyleKey: 'TITLE',
          typographyKey: 'headline',
          horizontalAlignment: 'center',
        },
      }),
      DROP_ZONE_1: newDropZonePinComponent({
        position: newVector(470, 120),
        size: newSize(360, 360),
        order: 'a1',
      }),
    },
    pinComponents: [],
  };
}

export function dragAndDropQuadDropZoneWithTextLayout({
  question = fallbackCopy['dragAndDrop.title'],
  backgroundColor,
  backgroundImage,
}: {
  question?: string;
  backgroundColor?: string;
  backgroundImage?: string;
}): DragAndDropPinLayoutTemplate {
  return {
    key: 'QUAD_DROP_ZONE_TEXT',
    backgroundColor,
    backgroundImage,
    layoutParts: {
      TITLE: newTextPinComponent({
        position: newVector(32, 32),
        size: newSize(896, 60),
        content: stringToProsemirror(question, {
          bold: true,
        }),
        order: 'a0',
        settings: {
          textStyleKey: 'TITLE',
          typographyKey: 'headline',
          horizontalAlignment: 'center',
        },
      }),
      DROP_ZONE_1: newDropZonePinComponent({
        position: newVector(511, 112),
        size: newSize(175, 175),
        order: 'a1',
      }),
      DROP_ZONE_2: newDropZonePinComponent({
        position: newVector(511, 333),
        size: newSize(175, 175),
        order: 'a2',
      }),
      DROP_ZONE_3: newDropZonePinComponent({
        position: newVector(734, 112),
        size: newSize(175, 175),
        order: 'a3',
      }),
      DROP_ZONE_4: newDropZonePinComponent({
        position: newVector(734, 333),
        size: newSize(175, 175),
        order: 'a4',
      }),
      TEXT_ITEM_1: newTextPinComponent({
        position: newVector(121, 404),
        size: newSize(175, 50),
        content: stringToProsemirror('Text'),
        order: 'a5',
      }),
      TEXT_ITEM_2: newTextPinComponent({
        position: newVector(121, 312),
        size: newSize(175, 50),
        content: stringToProsemirror('Text'),
        order: 'a6',
      }),
      TEXT_ITEM_3: newTextPinComponent({
        position: newVector(121, 229),
        size: newSize(175, 50),
        content: stringToProsemirror('Text'),
        order: 'a7',
      }),
      TEXT_ITEM_4: newTextPinComponent({
        position: newVector(121, 137),
        size: newSize(175, 50),
        content: stringToProsemirror('Text'),
        order: 'a8',
      }),
    },
    pinComponents: [],
  };
}

export function dragAndDropQuadDropZoneWithImageLayout({
  question = fallbackCopy['dragAndDrop.title'],
  backgroundColor,
  backgroundImage,
  imageOverride,
}: {
  question?: string;
  backgroundColor?: string;
  backgroundImage?: string;
  imageOverride?: ImagePinComponent | 'no-image';
}): DragAndDropPinLayoutTemplate {
  return {
    key: 'QUAD_DROP_ZONE_IMAGE',
    backgroundColor,
    backgroundImage,
    layoutParts: {
      TITLE: newTextPinComponent({
        position: newVector(32, 32),
        size: newSize(896, 60),
        content: stringToProsemirror(question, {
          bold: true,
        }),
        order: 'a0',
        settings: {
          textStyleKey: 'TITLE',
          typographyKey: 'headline',
          horizontalAlignment: 'center',
        },
      }),
      DROP_ZONE_1: newDropZonePinComponent({
        position: newVector(511, 112),
        size: newSize(175, 175),
        order: 'a1',
      }),
      DROP_ZONE_2: newDropZonePinComponent({
        position: newVector(511, 333),
        size: newSize(175, 175),
        order: 'a2',
      }),
      DROP_ZONE_3: newDropZonePinComponent({
        position: newVector(734, 112),
        size: newSize(175, 175),
        order: 'a3',
      }),
      DROP_ZONE_4: newDropZonePinComponent({
        position: newVector(734, 333),
        size: newSize(175, 175),
        order: 'a4',
      }),
      ...(imageOverride !== 'no-image' && {
        IMAGE1:
          imageOverride ||
          newImagePinComponent({
            position: newVector(215, 333),
            size: newSize(125, 125),
            order: 'a5',
          }),
      }),
      ...(imageOverride !== 'no-image' && {
        IMAGE2:
          imageOverride ||
          newImagePinComponent({
            position: newVector(68, 333),
            size: newSize(125, 125),
            order: 'a6',
          }),
      }),
      ...(imageOverride !== 'no-image' && {
        IMAGE3:
          imageOverride ||
          newImagePinComponent({
            position: newVector(215, 144),
            size: newSize(125, 125),
            order: 'a7',
          }),
      }),
      ...(imageOverride !== 'no-image' && {
        IMAGE4:
          imageOverride ||
          newImagePinComponent({
            position: newVector(69, 144),
            size: newSize(125, 125),
            order: 'a8',
          }),
      }),
    },
    pinComponents: [],
  };
}
